import { Alert, Stack, Typography } from '@mui/material';
import { hasEvacContent } from 'features/evacZones/utils/hasEvacContent';
import { PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { FormEvacZoneStatus } from 'shared/types';
import { removeHTMLTags } from 'shared/utils';

type SharedDeactivationAlertValues = {
  evacZoneStatuses: FormEvacZoneStatus[];
  manualDeactivationStarted: boolean | null;
  evacuationOrders: string;
  evacuationWarnings: string;
  evacuationAdvisories: string;
  evacuationNotes: string;
  customOrders: boolean;
  customWarnings: boolean;
  customAdvisories: boolean;
};

type BaseDeactivationAlertProps = PropsWithChildren<{
  hasEvacZones: boolean;
  isExternallyBlocked?: boolean;
  isExternallyTriggered?: boolean;
}>;

export const BaseDeactivationAlert = (
  props: BaseDeactivationAlertProps,
): JSX.Element | null => {
  const {
    hasEvacZones,
    isExternallyBlocked = false,
    isExternallyTriggered = false,
    children,
  } = props;
  const { t } = useTranslation();
  const { watch } = useFormContext<SharedDeactivationAlertValues>();

  const [
    evacZoneStatuses,
    manualDeactivationStarted,
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationNotes,
    customOrders,
    customWarnings,
    customAdvisories,
  ] = watch([
    'evacZoneStatuses',
    'manualDeactivationStarted',
    'evacuationOrders',
    'evacuationWarnings',
    'evacuationAdvisories',
    'evacuationNotes',
    'customOrders',
    'customWarnings',
    'customAdvisories',
  ]);

  const hasEvacuationOrders = hasEvacContent({
    evacContent: evacuationOrders,
    customEvac: customOrders,
    hasEvacZones,
  });
  const hasEvacuationWarnings = hasEvacContent({
    evacContent: evacuationWarnings,
    customEvac: customWarnings,
    hasEvacZones,
  });
  const hasEvacuationAdvisories = hasEvacContent({
    evacContent: evacuationAdvisories,
    customEvac: customAdvisories,
    hasEvacZones,
  });
  const hasEvacuationNotes = Boolean(removeHTMLTags(evacuationNotes ?? ''));

  const hasActiveEvacs =
    Boolean(evacZoneStatuses.length) ||
    hasEvacuationOrders ||
    hasEvacuationWarnings ||
    hasEvacuationAdvisories ||
    hasEvacuationNotes;

  const showDeactivationAlert =
    !isExternallyBlocked &&
    !hasActiveEvacs &&
    (Boolean(manualDeactivationStarted) || isExternallyTriggered);

  if (!showDeactivationAlert) {
    return null;
  }

  return (
    <Alert severity="info" icon={false}>
      <Stack spacing={1}>
        {children}

        <Typography>
          <Trans i18nKey="editGeoEvent.deactivationStarted.silentUpdate">
            <b>Silent Update</b> will be posted at time of deactivation.
          </Trans>
        </Typography>

        <Typography fontStyle="italic">
          {t('editGeoEvent.deactivationStarted.deactivated')}
        </Typography>
      </Stack>
    </Alert>
  );
};
